import React from 'react';
import { motion } from 'framer-motion';
import { Star, Mic, Heart, Target, CheckCircle, ArrowLeft, Lightbulb, XCircle, CheckSquare, Zap, Search, Send, HelpCircle, FileText, Video, ExternalLink } from 'lucide-react';
import { StepCard, VideoCard, VideoGrid, InfoCard } from './SectionComponents';

const SalesStandOutSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const elevatorPitchExample = `<em>"Hi, I'm Jess. I love making people say yes and smashing my targets. In the past 6 months alone, I've booked 100+ meetings by personalising at scale and constantly testing what works. I'm great at building instant connections, something I picked up going door-to-door selling solar panels, and I'm a bit of a psychology nerd, so I genuinely enjoy figuring out what makes people tick. I thrive in fast-paced, hustling B2B roles, which is why I'm now looking to join a high-growth startup where I can fully own outbound, push myself, and sell something I really believe in. And from everything I've seen, that's exactly what you offer." </em>`;

  const elevatorPitchElements = [
    "Your background — what roles have you done that are most relevant?",
    "Key results — targets hit, wins closed, learning moments",
    "Personal spark — something memorable (e.g. side hustle, sports, obsession)",
    "Why this role now — why you love sales and are obsessed with the idea of selling the product of the startup you're interviewing with"
  ];

  const elevatorPitchExamples = [
    {
      name: "Fatima, SDR @ Thrust Carbon",
      url: "https://www.loom.com/share/a7cd5f7d6fad4d3c8ca6165e15661fd5?sid=bc03f1b4-5695-4a29-bd65-3497d8188fff",
      creator: "Watch how she is super targeted on the company - her reference to the founder's quote from the spotlight is a great touch.",
      title: "Fatima's Elevator Pitch"
    },
    {
      name: "Giorgia, Sales Exec @ Karma Cans",
      url: "https://www.loom.com/share/097db1b460b84292851ade7c2c375f05?sid=f400fe34-a9e3-4307-a60b-153f8f77eadd",
      creator: "Watch how she shows her passion for the company and demonstrates additional research on the company culture.",
      title: "Giorgia's Elevator Pitch"
    },
    {
      name: "Milly, Sales Stream Lead @ Jumpstart",
      url: "https://www.loom.com/share/450c7fef62dc4391b368af6038098146?sid=3dbec334-1f9b-45f7-bcba-640b46d19566",
      creator: "Watch how she has a relaxed, conversational tone with her motivations for wanting to work in startup recruitment running through the whole pitch.",
      title: "Milly's Elevator Pitch"
    }
  ];

  const companyObsessionVideos = [
    {
      name: "Kabir - Autogen AI",
      url: "https://www.loom.com/share/bd3fada5603049a1b9e0093b6257ab39?sid=a2aefe84-9f10-408a-b9df-6a210bd24a8e",
      creator: "Watch how he combines industry knowledge, personal anecdotes, and specific details from spotlight to show his motivation.",
      title: "Kabir_Why Autogen_Sales interview training"
    },
    {
      name: "Milly - Jumpstart", 
      url: "https://www.loom.com/share/2ad7719755074a5792024e094dbb536a?sid=8397c7bc-fdab-4348-8d71-5daf2ea2f711",
      creator: "Watch how she shows understanding & fit with the company's values with 3 clearly structured rationales.",
      title: "Milly_Why Jumpstart_Sales Interview Training_"
    }
  ];

  const readyToHitGroundTips = [
    {
      icon: "🎯",
      title: "Come with Target Lists",
      description: "Come in with 5-10 names of companies or titles you'd target",
      details: "Explain why you'd go after those - industry, size, job title, pain points"
    },
    {
      icon: "📝",
      title: "Show Outreach Ideas",
      description: "Bonus: share 1-2 rough outreach messages or ideas",
      details: "This shows you've thought practically about how you'd start delivering value"
    }
  ];

  const storyTypes = [
    "Hitting or missing targets",
    "Handling rejection or a tough customer", 
    "Learning something new quickly"
  ];

  const goodQuestions = [
    '"You mentioned the role would be 60% outbound focused, I have actually been trying out a few techniques on LinkedIn to hook in warm leads — has anything like this worked for you in the past?"',
    '"You mentioned in podcast Y last year that you were planning to expand into the US imminently — is there any reason this hasn\'t happened yet? Seems like for reason XYZ it would be a perfect market"'
  ];

  const avoidQuestions = [
    "What's your favourite thing about working here?",
    "What was the inspiration behind this startup?",
    "What do you love most about your job?",
    "What's the culture like?",
    "What are the goals for the next 1–2 years?",
    "What are your company values?",
    "What is your biggest challenge at the moment?",
    "What are the top traits you are looking for in this hire?",
    "What are the typical hours/salary?"
  ];

  const thingsToAvoid = [
    {
      title: "Waffling",
      description: "Limit your answers to 2-3 points per answer, and make each answer no longer than 2 minutes. You can always ask to elaborate further if you need to"
    },
    {
      title: "Generic statements",
      description: 'You need to SHOW why you are all of these things, rather than just say them: "I am money motivated", "I am competitive", "I like hitting targets", "I\'m a people person", "I just love startups"'
    },
    {
      title: "CV speak", 
      description: "Talk like you would on a call, not in a cover letter"
    },
    {
      title: "No evidence",
      description: "Always back claims up with results or behaviour"
    },
    {
      title: "Over-polished answers",
      description: "Be confident, but sound human"
    }
  ];

  const checklistItems = [
    'Practiced your 60-sec elevator pitch (and got feedback on it)',
    'Researched the company — product, team, recent updates',
    'Prepared clear motivations for the company and the role',
    'Prepped 2-3 STAR stories covering key traits',
    'Prepared 2-3 strong, specific, well-researched questions to ask at the end',
    'Avoided generic language',
    'Booked in time with Gemma (roleplay & interview expert) or Channon (GTM & sales) to help you prepare',
    'Send a thank you email to your interviewers after your interview is over'
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              First interview: stand out early
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Master the art of first impressions and position yourself as the ideal sales candidate.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          {/* Overview Section */}
          <StepCard title="Overview">
            <div className="space-y-6">
              <p className="text-xl text-gray-800 leading-relaxed">
                👋 Welcome! Let's walk through how to ace your first interview.
              </p>
              
              {/* Interview Details - Compact horizontal layout */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Duration:</span>
                    <p className="text-gray-600 text-sm">Typically 30–40 minutes</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Interviewer:</span>
                    <p className="text-gray-600 text-sm">1–2 people (team lead &/or founder)</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Their goal:</span>
                    <p className="text-gray-600 text-sm">Assess if you're a strong, motivated fit</p>
                  </div>
                </div>
              </div>
              
              {/* Navigation Section */}
              <div>
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Your goals:</h4>
                <div className="space-y-2">
                  {[
                    { text: 'Nail your elevator pitch', section: 'goal-1', number: '1' },
                    { text: 'Show you\'re obsessed with the company', section: 'goal-2', number: '2' },
                    { text: 'Show you\'re great fit the role', section: 'goal-3', number: '3' },
                    { text: 'Ask good questions', section: 'goal-4', number: '4' }
                  ].map((goal, i) => (
                    <button
                      key={i}
                      onClick={() => scrollToSection(goal.section)}
                      className="flex items-center space-x-3 w-full text-left p-3 rounded-lg border border-gray-300 hover:bg-[#02B491] hover:text-white hover:border-[#02B491] transition-all duration-200 group bg-white cursor-pointer shadow-sm hover:shadow-md"
                    >
                      <div className="flex-shrink-0 w-6 h-6 bg-gray-100 group-hover:bg-white group-hover:text-[#02B491] rounded-full flex items-center justify-center text-xs font-bold text-gray-700">
                        {goal.number}
                      </div>
                      <span className="text-gray-800 group-hover:text-white font-medium">{goal.text}</span>
                      <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>
          
          <div id="goal-1">
            <StepCard title="🎤 Goal 1: Nail your elevator pitch">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Your pitch should be <span className="font-semibold text-gray-800">short, sharp and focused</span>. Aim for 60-90 seconds covering:
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Aim for 60-90 seconds that covers:</h4>
                <div className="grid gap-4">
                  {elevatorPitchElements.map((element, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                        <span className="text-white font-bold text-sm">{i + 1}</span>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{element}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <InfoCard
                icon={Lightbulb}
                title="Perfect Example"
                description={elevatorPitchExample}
                type="tip"
              />

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Check out these best practice examples:</h4>
                <VideoGrid videos={elevatorPitchExamples} columns={3} />
              </div>
            </div>
            </StepCard>
          </div>

          <div id="goal-2">
            <StepCard title="💡 Goal 2: Show you're obsessed with the company">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Vague lines like <span className="line-through">"I love your mission"</span> won't cut it. 
                Instead, give <span className="font-semibold text-gray-800">3 specific reasons</span> why you're excited about this company.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">That might include:</h5>
                <div className="space-y-3">
                  {[
                    'A podcast, blog post, or cold email they sent',
                    'A product, customer segment, or GTM angle you find compelling',
                    'A clear link between your past experience and what they\'re selling'
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h5 className="font-bold text-gray-900 mb-3 text-lg">Example:</h5>
                <p className="text-gray-700 leading-relaxed italic">
                  "I listened to your founder on the XYZ Podcast and was really struck by how you're rethinking finance automation from a workflow-first perspective. The pain point you're solving is exactly what I'm seeing at my current role, where I helped launch a tool to streamline expense approvals. It really resonates with me and I'd love to help scale it."
                </p>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Check out these best practice examples:</h4>
                <VideoGrid videos={companyObsessionVideos} columns={2} />
              </div>
            </div>
            </StepCard>
          </div>

          <div id="goal-3">
            <StepCard title="🎯 Goal 3: Show you're a great fit for the role">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Make it easy for the interviewer to imagine you doing the work from day one. 
                How? Follow these two steps.
              </p>
              
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Step 1: Start with the JD</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800">Go through the job description to find the key traits they're looking for.</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800">Prepare 2–3 specific examples for each trait from your experience.</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800">Pitching yourself in 60-90 seconds is also proof that you can sell!</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">Here's what that might look like:</h5>
                <div className="space-y-4">
                  {[
                    {
                      title: "Take the initiative",
                      example: "Spotted a gap in our CRM tracking, built a simple Airtable to plug it, and shared it with the team."
                    },
                    {
                      title: "Learn fast", 
                      example: "Picked up HubSpot in under a week after switching from Salesforce and taught it to the rest of the team."
                    },
                    {
                      title: "Handle tough moments",
                      example: "Rebounded after missing Q1 target by mapping out where I went wrong and doubling down the next. Ended up top rep."
                    }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-[#02B491] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white font-bold text-xs">→</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">{item.title}</span>
                        <span className="text-gray-600"> → {item.example}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Step 2: Go beyond the JD</h4>
                <p className="text-gray-700 leading-relaxed mb-6">
                  <span className="font-semibold">Show them you're ready to hit the ground running</span>
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {readyToHitGroundTips.map((tip, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xl">{tip.icon}</span>
                        <h5 className="font-bold text-gray-900">{tip.title}</h5>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-2 text-sm">{tip.description}</p>
                      <p className="text-gray-500 text-xs">{tip.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </StepCard>
          </div>

          <div id="goal-4">
            <StepCard title="❓ Goal 4: Show you're invested by asking good questions">
            <div className="space-y-10">
              <p className="text-gray-600 text-lg leading-relaxed">
                <span className="font-semibold text-gray-800">Ask questions the way you'd sell</span> — based on curiosity and insight.
              </p>

              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <h4 className="font-semibold text-gray-900 text-lg">Try questions like these:</h4>
                </div>
                <div className="space-y-4">
                  {goodQuestions.map((question, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border-l-4 border-green-400">
                      <p className="text-gray-700 leading-relaxed font-semibold text-base">
                        {question}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <h4 className="font-semibold text-gray-900 text-lg">Things to avoid</h4>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {avoidQuestions.map((question, i) => (
                    <div key={i} className="text-gray-700 leading-relaxed font-semibold text-base">
                      "{question}"
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2">
                  <p className="text-gray-600">Also avoid:</p>
                  <ul className="space-y-1 ml-4">
                    <li className="text-gray-600">• Questions that can be answered on the company website</li>
                    <li className="text-gray-600">• Anything too focused on salary, hours or remote policy (save those for later)</li>
                  </ul>
                </div>
              </div>
            </div>
          </StepCard>
          </div>

          <StepCard title="❌ Things to avoid in early interviews">
            <div className="space-y-6">
              <div className="grid gap-6">
                {thingsToAvoid.map((item, i) => (
                  <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <h5 className="font-bold text-red-900 mb-3 text-lg">{item.title}</h5>
                    <p className="text-red-800 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="📋 First interview checklist">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed">
                When preparing for your 1st round interview, make sure you've:
              </p>
              
              <div className="grid gap-3">
                {checklistItems.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                    <CheckSquare className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8">
                <p className="text-blue-800">
                  <span className="font-semibold">Feeling stuck or not getting interviews?</span> Reach out to info@jumpstart-uk.com for support.
                </p>
              </div>
            </div>
          </StepCard>
        </div>
      </div>

      {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default SalesStandOutSection; 